import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { Group, Market } from "./marketSlice.types";
import { normalizeMarkets } from "./utils";
import { updateMarketColor } from "./market.thunk";

const marketsAdapter = createEntityAdapter<Market, number>({
  // @ts-ignore
  selectId: (market: Market) => market.id,
  sortComparer: (a: Market, b: Market) =>
    a.position && b.position ? a.position - b.position : 0,
});

const initialMarketsState = marketsAdapter.getInitialState();

const groupsAdapter = createEntityAdapter<Group, number>({
  // @ts-ignore
  selectId: (brand: Group) => brand.id,
  sortComparer: (a: Group, b: Group) => {
    const aIsUS = a.name.toLowerCase().startsWith("us");
    const bIsUS = b.name.toLowerCase().startsWith("us");

    if (aIsUS && !bIsUS) {
      return -1;
    }
    if (!aIsUS && bIsUS) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  },
});

const initialGroupsState = groupsAdapter.getInitialState();

export const marketsSlice = createSlice({
  name: "markets",
  initialState: {
    markets: initialMarketsState,
    groups: initialGroupsState,
  },
  reducers: {
    setMarkets: (state, action: PayloadAction<any>) => {
      if (action.payload.markets) {
        marketsAdapter.setAll(state.markets, action.payload.markets);
      }
      if (action.payload.groups) {
        groupsAdapter.setAll(state.groups, action.payload.groups);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateMarketColor.fulfilled, (state, action) => {
      if (action.payload !== null) {
        marketsAdapter.updateOne(state.markets, {
          id: action.payload.marketId,
          changes: {
            color: action.payload.color,
          },
        });
      }
    });
  },
});

export type MarketAdapterType = EntityState<Market, number>;
export type MarketGroupAdapterType = EntityState<Group, number>;

export type MarketsAdapterType = {
  markets: EntityState<Market, number>;
  groups: EntityState<Group, number>;
};

export const { setMarkets } = marketsSlice.actions;

export default marketsSlice.reducer;
